
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Download, Filter, FileText, Bell, ShoppingBag, CheckCircle, Clock, XCircle, CreditCard, BarChart, Package } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";

// Sample data for the sales list
const salesData = [
  { id: "ORD-001", buyer: "Ahmad Bin Ali", paymentMode: "Online", status: "Paid", itemCount: 3, amount: 156.50, date: "2025-05-11", time: "09:15 AM", channel: "JimatShop", type: "JimatShop" },
  { id: "ORD-002", buyer: "Siti Aminah", paymentMode: "In-Store", status: "Ready for Pick-up", itemCount: 2, amount: 89.75, date: "2025-05-11", time: "10:22 AM", channel: "JimatShop", type: "Walk-In" },
  { id: "ORD-003", buyer: "Raj Kumar", paymentMode: "Online", status: "Completed", itemCount: 5, amount: 245.30, date: "2025-05-11", time: "11:45 AM", channel: "JimatShop", type: "JimatShop" },
  { id: "ORD-004", buyer: "Lim Wei Ling", paymentMode: "In-Store", status: "Paid", itemCount: 1, amount: 67.25, date: "2025-05-11", time: "01:12 PM", channel: "JimatShop", type: "Walk-In" },
  { id: "ORD-005", buyer: "Muhammad Hafiz", paymentMode: "Online", status: "Completed", itemCount: 4, amount: 189.99, date: "2025-05-11", time: "02:30 PM", channel: "JimatShop", type: "JimatShop" },
  { id: "ORD-006", buyer: "Tan Mei Yen", paymentMode: "In-Store", status: "Ready for Pick-up", itemCount: 3, amount: 124.50, date: "2025-05-11", time: "03:45 PM", channel: "JimatShop", type: "Walk-In" },
  { id: "ORD-007", buyer: "Nurul Huda", paymentMode: "Online", status: "Paid", itemCount: 2, amount: 76.25, date: "2025-05-11", time: "04:20 PM", channel: "JimatShop", type: "JimatShop" },
  { id: "ORD-008", buyer: "Chen Wei", paymentMode: "In-Store", status: "Ready for Pick-up", itemCount: 6, amount: 210.75, date: "2025-05-11", time: "05:10 PM", channel: "JimatShop", type: "Walk-In" },
];

// Notifications data
const notifications = [
  { id: 1, orderId: "ORD-009", buyer: "Aisha Binti Mohamed", time: "10 minutes ago", message: "New order received" },
  { id: 2, orderId: "ORD-007", buyer: "Nurul Huda", time: "45 minutes ago", message: "Payment completed" },
  { id: 3, orderId: "ORD-006", buyer: "Tan Mei Yen", time: "1 hour ago", message: "Order ready for pickup" },
];

// Analytics data
const analyticsData = {
  topBuyers: [
    { name: "Ahmad Bin Ali", orderCount: 12, totalSpent: 1456.50 },
    { name: "Siti Aminah", orderCount: 8, totalSpent: 978.25 },
    { name: "Muhammad Hafiz", orderCount: 7, totalSpent: 845.00 },
  ],
  bestSellingSkus: [
    { sku: "SKU-12345", name: "Rice 5kg", unitsSold: 245 },
    { sku: "SKU-67890", name: "Milo 1kg", unitsSold: 187 },
    { sku: "SKU-54321", name: "Cooking Oil 2L", unitsSold: 156 },
  ],
  fastMovingBrands: [
    { brand: "Saji", unitsSold: 456 },
    { brand: "Nestle", unitsSold: 378 },
    { brand: "Maggi", unitsSold: 289 },
  ],
  averageBasketSize: 4.2,
  repeatPurchaseRate: 68.5,
};

// Stats data
const statsData = {
  daily: {
    transactions: 82,
    averageOrderValue: 115.35,
    jimatshopSales: 4250.50,
    walkInSales: 3785.25
  },
  weekly: {
    transactions: 523,
    averageOrderValue: 118.65,
    jimatshopSales: 31450.75,
    walkInSales: 30520.80
  },
  monthly: {
    transactions: 2140,
    averageOrderValue: 122.45,
    jimatshopSales: 135600.50,
    walkInSales: 126250.75
  },
  quarterly: {
    transactions: 6420,
    averageOrderValue: 125.75,
    jimatshopSales: 410500.25,
    walkInSales: 395800.50
  },
  yearly: {
    transactions: 25680,
    averageOrderValue: 127.85,
    jimatshopSales: 1650000.75,
    walkInSales: 1630000.25
  }
};

const SalesJimatshop = () => {
  const [duration, setDuration] = useState<"daily" | "weekly" | "monthly" | "quarterly" | "yearly">("daily");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("all-sales");

  const handleDurationChange = (value: string) => {
    setDuration(value as "daily" | "weekly" | "monthly" | "quarterly" | "yearly");
  };

  const filteredSales = salesData.filter(sale => {
    const matches = 
      sale.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.buyer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.date.includes(searchTerm);
      
    if (selectedTab === "all-sales") return matches;
    if (selectedTab === "processing") return matches && sale.status === "Paid";
    if (selectedTab === "ready-to-pickup") return matches && sale.status === "Ready for Pick-up";
    if (selectedTab === "fulfilled") return matches && sale.status === "Completed";
    return matches;
  });

  const currentStats = statsData[duration];

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Paid": return "bg-blue-100 text-blue-700";
      case "Ready for Pick-up": return "bg-amber-100 text-amber-700";
      case "Completed": return "bg-green-100 text-green-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Sales - Jimatshop</h1>
          <p className="text-muted-foreground">
            Manage and monitor sales for Jimatshop.
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" className="border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
            <Filter className="mr-2 h-4 w-4" />
            Filter
          </Button>
          <Button variant="outline" className="border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
            <Calendar className="mr-2 h-4 w-4" />
            Date Range
          </Button>
          <Button variant="outline" className="border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Notifications Card */}
      <Card className="shadow-sm border-[#8CC84B]/20">
        <CardHeader className="bg-[#F2FCE2] border-b border-[#8CC84B]/20 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center">
            <Bell className="h-5 w-5 mr-2 text-[#4D9E41]" />
            Latest Orders from JimatShop Online
          </CardTitle>
          <Button variant="ghost" size="sm" className="text-[#4D9E41] hover:text-[#4D9E41] hover:bg-[#F2FCE2]/70">
            View All
          </Button>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {notifications.map(notification => (
              <Card key={notification.id} className="border-[#8CC84B]/10 bg-white shadow-sm">
                <CardContent className="p-4 flex items-start space-x-4">
                  <div className="rounded-full bg-[#F2FCE2] p-2 flex-shrink-0">
                    <ShoppingBag className="h-5 w-5 text-[#4D9E41]" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium">{notification.message}</p>
                    <p className="text-sm text-muted-foreground">
                      Order {notification.orderId} - {notification.buyer}
                    </p>
                    <p className="text-xs text-muted-foreground">{notification.time}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-[#8CC84B]/20">
        <CardHeader className="bg-[#F2FCE2] border-b border-[#8CC84B]/20">
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-[#4D9E41]" />
            Sales Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Filter by Duration</h3>
              <Select value={duration} onValueChange={handleDurationChange}>
                <SelectTrigger className="w-[180px] border-[#8CC84B]/20">
                  <SelectValue placeholder="Select Duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="border-[#8CC84B]/20 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{currentStats.transactions}</div>
                </CardContent>
              </Card>
              
              <Card className="border-[#8CC84B]/20 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">RM {currentStats.averageOrderValue.toFixed(2)}</div>
                </CardContent>
              </Card>
              
              <Card className="border-[#8CC84B]/20 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">JimatShop Sales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">RM {currentStats.jimatshopSales.toFixed(2)}</div>
                </CardContent>
              </Card>
              
              <Card className="border-[#8CC84B]/20 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Walk-In Sales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">RM {currentStats.walkInSales.toFixed(2)}</div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Tabs 
                defaultValue="all-sales" 
                value={selectedTab}
                onValueChange={(value) => setSelectedTab(value)}
                className="mb-6"
              >
                <TabsList className="mb-4 bg-white border rounded-sm overflow-auto whitespace-nowrap w-full flex">
                  <TabsTrigger value="all-sales" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    All Sales
                  </TabsTrigger>
                  <TabsTrigger value="processing" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Processing
                  </TabsTrigger>
                  <TabsTrigger value="ready-to-pickup" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Ready to pickup
                  </TabsTrigger>
                  <TabsTrigger value="fulfilled" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Fulfilled
                  </TabsTrigger>
                  <TabsTrigger value="cancelled" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Cancelled
                  </TabsTrigger>
                  <TabsTrigger value="payment-status" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Payment Status
                  </TabsTrigger>
                  <TabsTrigger value="analytics" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Analytics
                  </TabsTrigger>
                  <TabsTrigger value="jimatshop-order" className="text-sm data-[state=active]:bg-[#F2FCE2] data-[state=active]:text-[#4D9E41]">
                    Jimatshop Order
                  </TabsTrigger>
                </TabsList>

                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">
                    {selectedTab === "analytics" ? "Sales Analytics" : "Sales List"}
                  </h3>
                  {selectedTab !== "analytics" && (
                    <div className="w-64">
                      <Input 
                        placeholder="Search by ID, buyer or date"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-[#8CC84B]/20"
                      />
                    </div>
                  )}
                </div>

                <TabsContent value="analytics" className="mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="border-[#8CC84B]/20 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Top Buyers</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {analyticsData.topBuyers.map((buyer, index) => (
                            <li key={index} className="flex justify-between items-center">
                              <span className="font-medium">{buyer.name}</span>
                              <span className="text-sm text-muted-foreground">
                                {buyer.orderCount} orders - RM {buyer.totalSpent.toFixed(2)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-[#8CC84B]/20 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Best-Selling SKUs</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {analyticsData.bestSellingSkus.map((item, index) => (
                            <li key={index} className="flex justify-between items-center">
                              <div>
                                <div className="font-medium">{item.name}</div>
                                <div className="text-xs text-muted-foreground">{item.sku}</div>
                              </div>
                              <span className="text-sm">{item.unitsSold} units</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-[#8CC84B]/20 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Fast-Moving Brands</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          {analyticsData.fastMovingBrands.map((brand, index) => (
                            <li key={index} className="flex justify-between items-center">
                              <span className="font-medium">{brand.brand}</span>
                              <span className="text-sm">{brand.unitsSold} units</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="border-[#8CC84B]/20 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Avg. Basket Size</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="text-3xl font-bold">{analyticsData.averageBasketSize}</div>
                        <p className="text-sm text-muted-foreground">Items per order</p>
                      </CardContent>
                    </Card>

                    <Card className="border-[#8CC84B]/20 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Repeat Purchase Rate</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="text-3xl font-bold">{analyticsData.repeatPurchaseRate}%</div>
                        <p className="text-sm text-muted-foreground">Returning customers</p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="all-sales" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>

                <TabsContent value="processing" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>

                <TabsContent value="ready-to-pickup" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>

                <TabsContent value="fulfilled" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>

                <TabsContent value="cancelled" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>

                <TabsContent value="payment-status" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>

                <TabsContent value="jimatshop-order" className="mt-0">
                  <OrderTable orders={filteredSales} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Extract OrderTable component for better organization
const OrderTable = ({ orders }: { orders: any[] }) => {
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Paid": return "bg-blue-100 text-blue-700";
      case "Ready for Pick-up": return "bg-amber-100 text-amber-700";
      case "Completed": return "bg-green-100 text-green-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Paid": return <CreditCard className="h-3 w-3 mr-1" />;
      case "Ready for Pick-up": return <Clock className="h-3 w-3 mr-1" />;
      case "Completed": return <CheckCircle className="h-3 w-3 mr-1" />;
      case "Cancelled": return <XCircle className="h-3 w-3 mr-1" />;
      default: return null;
    }
  };

  return (
    <div className="border rounded-md border-[#8CC84B]/20">
      <Table>
        <TableHeader className="bg-[#F2FCE2]">
          <TableRow>
            <TableHead className="text-[#4D9E41]">Order ID</TableHead>
            <TableHead className="text-[#4D9E41]">Buyer Name</TableHead>
            <TableHead className="text-[#4D9E41]">Payment Mode</TableHead>
            <TableHead className="text-[#4D9E41]">Status</TableHead>
            <TableHead className="text-center text-[#4D9E41]">Total Items</TableHead>
            <TableHead className="text-[#4D9E41]">Total Value (RM)</TableHead>
            <TableHead className="text-[#4D9E41]">Date & Time</TableHead>
            <TableHead className="text-[#4D9E41]">Channel</TableHead>
            <TableHead className="text-right text-[#4D9E41]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.length > 0 ? (
            orders.map((order) => (
              <TableRow key={order.id} className="hover:bg-[#F2FCE2]/50">
                <TableCell className="font-medium">{order.id}</TableCell>
                <TableCell>{order.buyer}</TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                    order.paymentMode === "Online" 
                      ? "bg-[#E5F5DA] text-[#8CC84B]"
                      : "bg-[#F2FCE2] text-[#4D9E41]"
                  }`}>
                    {order.paymentMode}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getStatusBadgeColor(order.status)}`}>
                    {getStatusIcon(order.status)}
                    {order.status}
                  </span>
                </TableCell>
                <TableCell className="text-center">{order.itemCount}</TableCell>
                <TableCell>{order.amount.toFixed(2)}</TableCell>
                <TableCell>{`${order.date}, ${order.time}`}</TableCell>
                <TableCell>{order.channel}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" className="h-8 px-2 text-xs">
                      View Details
                    </Button>
                    <Button size="sm" className="h-8 px-2 text-xs bg-[#4D9E41] hover:bg-[#3C7A33]">
                      Process Order
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                No orders found matching your search criteria.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SalesJimatshop;
